import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { format, isValid } from "date-fns";
import { Cruise } from "main/javascripts/types/cruise";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontSize,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  accentColor,
  borderColor,
} from "main/javascripts/styles/base/colorStyle";
import { parseDate } from "main/javascripts/utils/DateUtil";
import { CruiseScheduleListItem } from "./CruiseScheduleListItem";

export interface IProps {
  cruise: Cruise.Cruise;
  limit?: number;
  hideDetailButton?: boolean;
}

export const CruiseScheduleList: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { cruise, limit, hideDetailButton } = props;

  const { t, i18n } = useTranslation(["component", "label", "unit"]);

  const [isExpanded, setIsExpanded] = useState(false);
  const initialShowCount = 2;

  const schedules = limit
    ? (cruise.cruiseSchedules || []).slice(0, limit)
    : cruise.cruiseSchedules || [];

  const onExpandButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const formatDate = (dateString: string) => {
    const date = parseDate(dateString);
    if (isValid(date)) {
      return format(
        date,
        i18n.language === "ja" ? "yyyy/MM/dd" : "MMMM d, yyyy"
      );
    }
    return dateString;
  };

  if (schedules.length === 0) {
    return null;
  }

  let expandButtonText = t("button:common.showLess");
  let expandButtonStyles = expandCloseButtonStyle;
  if (!isExpanded && schedules.length > initialShowCount) {
    const dateString = formatDate(schedules[initialShowCount].departureDate);
    expandButtonText = t("button:cruise.showDeparturesStartingFrom", {
      date: dateString,
    });
    expandButtonStyles = expandButtonStyle;
  }

  return (
    <div>
      {schedules.map((schedule, index) => {
        if (!isExpanded && index >= initialShowCount) {
          return null;
        }
        return (
          <CruiseScheduleListItem
            key={schedule.id}
            uniqueId={cruise.uniqueId}
            schedule={schedule}
            hideDetailButton={hideDetailButton}
          />
        );
      })}
      {schedules.length > initialShowCount && !hideDetailButton && (
        <div css={expandButtonBlockStyle}>
          <a css={expandButtonStyles} onClick={onExpandButtonClick}>
            {expandButtonText}
          </a>
        </div>
      )}
    </div>
  );
};

const expandButtonBlockStyle = css`
  padding: ${space.atom} ${space.atom2_5x};
  display: flex;
  justify-content: center;
  border-top: 1px solid ${borderColor.primaryLightColor};
`;
const expandButtonStyle = css`
  position: relative;
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${accentColor.primaryColor};
  padding: ${space.atom0_5x} ${space.atom3x} ${space.atom0_5x} 0;
  border-bottom: 1px solid;
  margin-bottom: ${space.atom};
  cursor: pointer;
  transition: opacity 0.3s ease;
  &:hover {
    opacity: 0.8;
  }
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    right: ${space.atom0_5x};
    display: block;
    width: 0.5rem;
    height: 0.5rem;
    border: 1px solid;
    border-left: 0;
    border-bottom: 0;
    transform: translateY(-75%) rotate(135deg);
    transition: transform 0.3s ease;
  }
`;
const expandCloseButtonStyle = css`
  ${expandButtonStyle};
  &::before {
    transform: translateY(-25%) rotate(-45deg);
  }
`;
